import React, { FunctionComponent, useContext, useEffect, useState } from 'react';
import {  Typography, FormControl, InputLabel, Select, MenuItem, Box } from '@mui/material';
import SubsidiaryService from '../../../shared/services/subsidiary/subsidiary.service';
import { ISubsidiary } from '../../../shared/services/subsidiary/subsidiary.model';
import { getSelectableSubsidiaries } from '../../../shared/utils/subsidiary-utils';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import { ScreenLabelsContext } from '../../../context/screen-labels/screen-labels';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';

// THIS COMPONENT NEEDS TO BE UPDATED TO USE FORMIK

interface Address {
  id?: string;
  addressId?: string;
  text?: string;
  addressLine1?: string;
  addressLineOne?: string;
  addressLine2?: string;
  city?: string;
  town?: string;
  postcode?: string;
  postCode?: string;
}


export const AddressSelectionDialog: FunctionComponent<{
  open: boolean;
  onClose: () => void;
  searchResults: Address[];
  selectedAddressId: string;
  onAddressChange: (addressId: string) => void;
  onConfirm: (subsidiaryId?: string, fuelType?: string, periodStartDate?: string, periodEndDate?: string) => void;
  existingMeters?: any[];
  locationId?: string;
}> = ({
  open,
  onClose,
  searchResults,
  selectedAddressId,
  onAddressChange,
  onConfirm,
  existingMeters = [],
  locationId
}) => {
    //Context
    const userDetailsContext = useContext(UserDetailsContext)
    const organisationDetails = userDetailsContext.userDetails?.organisation
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>([]);
    const [selectedSubsidiaryId, setSelectedSubsidiaryId] = useState<string>('');
    const [selectedFuelType, setSelectedFuelType] = useState<string>('elec');
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
      const subsidiaryService = SubsidiaryService.getInstance();
      subsidiaryService.getAll()
        .then(response => {
          setSubsidiaries(response);
          if (response.length === 1) {
            setSelectedSubsidiaryId(response[0].id);
          }
        })
        .catch(err => {
          console.error(err);
        });
    }, []);

    const handleConfirm = () => {
      setErrorMessage('');
      const existingPerseMeter = existingMeters.find(meter =>
        meter.source === 'PERSE' &&
        meter.perseEnergyCode === selectedFuelType &&
        meter.locationId === locationId
      );
      if (existingPerseMeter) {
        const fuelTypeName = selectedFuelType === 'elec' ? 'electricity' : 'gas';
        setErrorMessage(`You already have a linked ${fuelTypeName} meter for this location.`);
        return;
      }
      onConfirm(
        selectedSubsidiaryId || undefined,
        selectedFuelType
      );
    };

    return (
      <DialogWrapper open={open} onClose={onClose} title="Select Address" cancelHandler={onClose} submitHandler={handleConfirm} isSubmitting={false}>
        <Typography sx={{ mb: 2 }}>
          Please select an address from the search results to get meter details
        </Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Address</InputLabel>
          <Select
            value={selectedAddressId}
            onChange={(e) => onAddressChange(e.target.value)}
            label="Address"
          >
            {searchResults.map((address, index) => (
              <MenuItem key={address.id || index} value={address.id || address.addressId}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {address.text || address.addressLine1 || address.addressLineOne || 'Address Line 1'}
                    {address.addressLine2 && `, ${address.addressLine2}`}
                    {(address.city || address.town) && `, ${address.city || address.town}`}
                    {(address.postcode || address.postCode) && `, ${address.postcode || address.postCode}`}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {subsidiaries && (organisationDetails?.enableSubsidiaries || subsidiaries.length > 1) &&
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel htmlFor='subsidiaryName'>{screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}</InputLabel>
            <Select
              id='subsidiaryName'
              label={screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })} name='subsidiaryName'
              value={selectedSubsidiaryId}
              onChange={(e) => setSelectedSubsidiaryId(e.target.value)}
              sx={{ color: 'text.primary' }}
            >
              {getSelectableSubsidiaries(subsidiaries).sort((a, b) => a.name.localeCompare(b.name)).map((subsidiary, index) =>
                <MenuItem key={index} value={subsidiary.id}>{subsidiary.name}</MenuItem>
              )}
            </Select>
          </FormControl>
        }
        <FormControl fullWidth>
          <InputLabel>Fuel Type</InputLabel>
          <Select
            value={selectedFuelType}
            onChange={(e) => setSelectedFuelType(e.target.value)}
            label="Fuel Type"
            disabled={true}
          >
            <MenuItem value="elec">Electricity</MenuItem>
            <MenuItem value="gas">Gas</MenuItem>
          </Select>
        </FormControl>
        {errorMessage && (
          <Typography variant="body2" color="error" sx={{ mt: 2, textAlign: 'center' }}>
            {errorMessage}
          </Typography>
        )}
      </DialogWrapper>
    );
  };
